// @givmo/payments — CSP / script-integrity guidance (baked in, not polish).
//
// PCI DSS 4.0 6.4.3 (authorize + integrity-check every payment-page script) and 11.6.1
// (detect tampering of payment-page HTTP headers + script contents) make a Content-
// Security-Policy a REQUIREMENT for any surface that renders Shift4 Components or a wallet
// button — not a nice-to-have. The new web platform sets the CSP baseline that the Flutter
// apps never had (03 §8 "no CSP/security headers exist today — this sets the baseline").
//
// These constants are the allowlist a consuming app folds into its own CSP. They are
// exported (not a hardcoded header string) so each app composes them with its own
// app-specific sources, and so the host list stays in lock-step with `SHIFT4_JS_SRC`.
//
// SRI is intentionally NOT used for Shift4.js: Shift4 ships an unversioned bundle they
// update frequently, so a pinned `integrity` hash would break on their next push. The
// integrity control for payment scripts is the CSP `script-src` allowlist (this list),
// which is the sanctioned PCI 4.0 6.4.3 mechanism for externally-hosted, mutable scripts.

import { SHIFT4_JS_SRC } from './script-loader';

/** The Shift4.js host (origin) — derived from the single canonical script URL. */
export const SHIFT4_JS_HOST = new URL(SHIFT4_JS_SRC).origin; // https://js.dev.shift4.com

/** Apple Pay JS SDK host (the `<apple-pay-button>` web component + JS). */
export const APPLE_PAY_HOST = 'https://applepay.cdn-apple.com' as const;

/**
 * Google Pay JS API host (`pay.js` + the Google Pay button). Allow-listing the host covers the
 * SCRIPT only: the official button's CSS is injected as `<style>` elements, governed by `style-src`,
 * which no host can satisfy. Under a nonce-only `style-src` the consuming app MUST construct
 * `PaymentsClient` with `PaymentOptions.nonce` (and put the nonce on the `pay.js` tag) — Google
 * applies it to every element it injects. The donate app ships its per-request nonce as
 * `window.ENV.cspNonce` for exactly this (`apps/donate/app/lib/google-pay.client.ts`).
 */
export const GOOGLE_PAY_HOST = 'https://pay.google.com' as const;

/**
 * Google Pay availability-probe host. The observed staging violation for
 * `https://google.com/pay` showed that the Google Pay SDK probes the apex `google.com` origin
 * alongside `pay.google.com` (a multi-origin flow).
 */
export const GOOGLE_PAY_APEX_HOST = 'https://google.com' as const;

/** Shift4 API host the wallet/token flows talk to (`connect-src`). */
export const SHIFT4_API_HOST = 'https://api.shift4.com' as const;

/**
 * `script-src` hosts a payment surface must allow: Shift4.js + Apple Pay SDK + Google Pay
 * JS. (Apple Pay merchant validation goes through our own backend, not these hosts.)
 */
export const PAYMENTS_CSP_SCRIPT_SRC = [SHIFT4_JS_HOST, APPLE_PAY_HOST, GOOGLE_PAY_HOST] as const;

/**
 * `frame-src` hosts: Shift4 Components render their own PCI iframes from the Shift4 host
 * (the SAQ-A mechanism, by design); the Google Pay sheet renders from `pay.google.com`.
 */
export const PAYMENTS_CSP_FRAME_SRC = [SHIFT4_JS_HOST, GOOGLE_PAY_HOST] as const;

/**
 * `connect-src` hosts: tokenization / wallet calls reach `api.shift4.com`; Google Pay's
 * availability probe reaches the apex `google.com` origin. The per-env Apple Pay
 * merchant-validation endpoint (our backend) is app-specific config and is added by the consuming
 * app, not listed here.
 */
export const PAYMENTS_CSP_CONNECT_SRC = [SHIFT4_API_HOST, GOOGLE_PAY_APEX_HOST] as const;

export interface PaymentsCspDirectives {
  'script-src': readonly string[];
  'frame-src': readonly string[];
  'connect-src': readonly string[];
}

/**
 * The payment-surface CSP directive fragments a consuming app merges into its own policy.
 * Returns plain arrays so the app can spread them alongside its `'self'` and app sources:
 *   `script-src 'self' ${PAYMENTS_CSP.script-src.join(' ')}`.
 */
export const PAYMENTS_CSP: PaymentsCspDirectives = {
  'script-src': PAYMENTS_CSP_SCRIPT_SRC,
  'frame-src': PAYMENTS_CSP_FRAME_SRC,
  'connect-src': PAYMENTS_CSP_CONNECT_SRC,
};
